import React, { useState, useEffect } from 'react';
import './App.css';
import TanzakuForm from './components/TanzakuForm';
import TanzakuDisplay from './components/TanzakuDisplay';
import SasaBackground from './components/SasaBackground';
import Modal from './components/Modal';
import { getTanzaku, updateTanzakuPosition, deleteTanzaku } from './services/api';
import { saveToLocalStorage, loadFromLocalStorage } from './utils/localStorage';

function App() {
  const [tanzaku, setTanzaku] = useState([]);
  const [selectedTanzaku, setSelectedTanzaku] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cachedTanzaku = loadFromLocalStorage();
    if (cachedTanzaku.length > 0) {
      setTanzaku(cachedTanzaku);
      setLoading(false);
    }
    loadTanzaku();
  }, []);

  const loadTanzaku = async () => {
    try {
      setLoading(true);
      const data = await getTanzaku();
      const serverTanzaku = data.tanzaku || [];
      
      if (serverTanzaku.length > 0) {
        setTanzaku(serverTanzaku);
        saveToLocalStorage(serverTanzaku);
      } else {
        const cachedTanzaku = loadFromLocalStorage();
        if (cachedTanzaku.length > 0) {
          setTanzaku(cachedTanzaku);
        }
      }
      setError(null);
    } catch (err) {
      setError('短冊の読み込みに失敗しました');
      console.error('Error loading tanzaku:', err);
      const cachedTanzaku = loadFromLocalStorage();
      if (cachedTanzaku.length > 0) {
        setTanzaku(cachedTanzaku);
        setError('オフラインモード：キャッシュされた短冊を表示中');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTanzakuCreated = (newTanzaku) => {
    const updatedTanzaku = [newTanzaku, ...tanzaku];
    setTanzaku(updatedTanzaku);
    saveToLocalStorage(updatedTanzaku);
  };

  const handleTanzakuClick = (tanzaku) => {
    setSelectedTanzaku(tanzaku);
  };

  const handleCloseModal = () => {
    setSelectedTanzaku(null);
  };

  const handleTanzakuLiked = (updatedTanzaku) => {
    const newTanzaku = tanzaku.map(t => 
      t.id === updatedTanzaku.id 
        ? { ...t, likes: updatedTanzaku.likes } // 位置情報を保持しつつlikesのみ更新
        : t
    );
    setTanzaku(newTanzaku);
    saveToLocalStorage(newTanzaku);
    if (selectedTanzaku && selectedTanzaku.id === updatedTanzaku.id) {
      setSelectedTanzaku({ ...selectedTanzaku, likes: updatedTanzaku.likes });
    }
  };

  const handleTanzakuPositionChange = async (tanzakuId, newPosition) => {
    // ローカル状態を即座に更新（レスポンシブな感覚のため）
    const updatedTanzaku = tanzaku.map(t => 
      t.id === tanzakuId 
        ? { ...t, position_x: newPosition.x, position_y: newPosition.y }
        : t
    );
    setTanzaku(updatedTanzaku);
    saveToLocalStorage(updatedTanzaku);

    // バックエンドに位置を保存
    try {
      await updateTanzakuPosition(tanzakuId, newPosition);
    } catch (error) {
      console.error('位置保存エラー:', error);
      // エラーが発生した場合、元の位置に戻す
      const revertedTanzaku = tanzaku.map(t => 
        t.id === tanzakuId 
          ? t // 元の状態に戻す
          : t
      );
      setTanzaku(revertedTanzaku);
      saveToLocalStorage(revertedTanzaku);
    }
  };

  const handleTanzakuDelete = async (tanzakuId) => {
    // 削除確認ダイアログ
    if (!window.confirm('この短冊を削除してもよろしいですか？')) {
      return;
    }

    try {
      await deleteTanzaku(tanzakuId);
      // ローカル状態から削除
      const filteredTanzaku = tanzaku.filter(t => t.id !== tanzakuId);
      setTanzaku(filteredTanzaku);
      saveToLocalStorage(filteredTanzaku);
      // モーダルが開いている場合は閉じる
      if (selectedTanzaku && selectedTanzaku.id === tanzakuId) {
        setSelectedTanzaku(null);
      }
    } catch (error) {
      console.error('削除エラー:', error);
      alert('短冊の削除に失敗しました。');
    }
  };

  return (
    <div className="App">
      <div className="app-background">
        <SasaBackground />
      </div>
      
      <header className="app-header">
        <h1 className="app-title">七夕の願い事</h1>
        <p className="app-subtitle">あなたの願いを短冊に込めて、笹に飾りましょう</p>
      </header>

      <main className="app-main">
        <div className="content-container">
          <TanzakuForm onTanzakuCreated={handleTanzakuCreated} />
          
          {error && (
            <div className="error-message">
              {error}
              <button onClick={loadTanzaku} className="retry-button">
                再試行
              </button>
            </div>
          )}

          {loading ? (
            <div className="loading">短冊を読み込んでいます...</div>
          ) : (
            <TanzakuDisplay 
              tanzaku={tanzaku}
              onTanzakuClick={handleTanzakuClick}
              onTanzakuLiked={handleTanzakuLiked}
              onTanzakuPositionChange={handleTanzakuPositionChange}
            />
          )}
        </div>
      </main>

      {selectedTanzaku && (
        <Modal
          tanzaku={selectedTanzaku}
          onClose={handleCloseModal}
          onTanzakuLiked={handleTanzakuLiked}
          onTanzakuDelete={handleTanzakuDelete}
        />
      )}
    </div>
  );
}

export default App;